import { makeAutoObservable, runInAction } from 'mobx'

// evertel
import { decorate, inject, injectable } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { LogoutService } from '@evertel/session/LogoutService'
import { DeviceTokenService } from '@evertel/session/DeviceTokenService'
import { PushService } from '@evertel/push'
import { CurrentUserPollService, UnreadCountsState } from '@evertel/blue-user'
import { Analytics } from '@evertel/analytics'
import { ThemeState } from '@evertel/web/theme'
import { AuthBroadcastService } from '../AuthBroadcastService'
import { IdleTimerService } from '../IdleTimerService'

class AppService {

    isInitialized = false

    constructor(
        private sessionService: SessionService,
        private logoutService: LogoutService,
        private deviceTokenService: DeviceTokenService,
        private pushService: PushService,
        private currentUserPollService: CurrentUserPollService,
        private unreadCountsService: UnreadCountsState,
        private analytics: Analytics,
        private themeState: ThemeState,
        private authBroadcastService: AuthBroadcastService,
        private idleTimerService: IdleTimerService
    ) {
        makeAutoObservable(this)
    }

    // -----------------------------------------------
    // Pre-init (runs immediately, even on background tabs)
    // Loads theme to avoid bright-flash on dark-theme users,
    // and starts auth broadcast listener so background tabs
    // can receive cross-tab login/logout messages.
    // -----------------------------------------------
    preInit() {
        this.themeState.init()
        this.authBroadcastService.init()
    }

    // -----------------------------------------------
    // App-level init (called once when tab is active)
    // Mirrors mobile AppService.init()
    // -----------------------------------------------
    async init() {
        try {
            // Session service restores userID and authToken from localStorage
            await this.sessionService.init()

            // Device token service restores deviceToken from localStorage
            await this.deviceTokenService.init()

            // Analytics
            this.analytics.init()

            // Creates the logout interceptor
            this.logoutService.init()

            // Push service
            await this.pushService.init()

        } catch (e) {
            console.error('AppService init error:', e)
        }

        runInAction(() => {
            this.isInitialized = true
        })
    }

    // -----------------------------------------------
    // Authorized init (called when entering auth zone)
    // Mirrors mobile AppService.initAuthorized()
    // -----------------------------------------------
    async initAuthorized() {
        if (!this.sessionService.currentUserId) return

        // Re-read invite token & callback URL from localStorage.
        // Covers cross-tab scenarios where another tab/window set
        // these values after this tab's init() already ran.
        await this.sessionService.rehydratePendingState()

        // Broadcast login to other tabs for cross-tab auth coordination
        this.authBroadcastService.broadcastLogin(this.sessionService.currentUserId)

        // Register push token with server
        this.pushService.postPushToken()

        // Idle timer for CJIS-compliant session timeout
        this.idleTimerService.init()
        this.idleTimerService.setOnIdleCallback(() => {
            this.logoutService.logout('idle_timeout')
        })

        // Start polling services
        this.startPolls()

        // Fetch current user details
        await this.currentUserPollService.fetchUserDetails()
    }

    // -----------------------------------------------
    // Authorized cleanup (called when leaving auth zone)
    // Mirrors mobile AppService.destroyAuthorized()
    // -----------------------------------------------
    destroyAuthorized() {
        this.idleTimerService.destroyTimers()
        this.idleTimerService.clearTimeoutSetting()
        this.stopPolls()
    }

    // -----------------------------------------------
    // Full teardown
    // Mirrors mobile AppService.destroy()
    // -----------------------------------------------
    destroy() {
        this.stopPolls()
        this.idleTimerService.destroyTimers()
        this.authBroadcastService.destroy()
    }

    // -----------------------------------------------
    // Polls
    // -----------------------------------------------
    startPolls() {
        this.currentUserPollService.startPoll()
        this.unreadCountsService.startPoll()
    }

    stopPolls() {
        this.currentUserPollService.stopPoll()
        this.unreadCountsService.stopPoll()
    }

    // -----------------------------------------------
    // Debug info - mirrors mobile AppService.debugInfo()
    // Used by ContactSupportModal and error reporting.
    // -----------------------------------------------
    async debugInfo() {
        const pushToken = await this.pushService.getPushToken().catch(() => 'no push token')
        return {
            session: {
                userId: this.sessionService.currentUserId,
                selectedDepartmentId: this.sessionService.selectedDepartmentId,
                pushToken,
                navigation: {
                    currentPath: this.sessionService.currentPathname,
                    previousPath: this.sessionService.previousPath,
                    lastRoomOrThread: this.sessionService.previousRoomOrThread
                }
            },
            device: {
                agent: navigator.userAgent,
                platform: 'web',
                os: parseOS(navigator.userAgent)
            }
        }
    }

}

// -----------------------------------------------
// OS detection utility
// -----------------------------------------------
const OS_PATTERNS: [string, string][] = [
    ['windows phone', 'Windows Phone'],
    ['win', 'Windows'],
    ['iphone', 'iPhone'],
    ['ipad', 'iPad'],
    ['android', 'Android'],
    ['cros', 'ChromeOS'],
    ['mac', 'Macintosh'],
    ['linux', 'Linux']
]

function parseOS(userAgent: string): string {
    const ua = userAgent.toLowerCase()
    for (const [match, name] of OS_PATTERNS) {
        if (ua.includes(match)) return name
    }
    return 'unknown'
}

let i = 0
decorate(injectable(), AppService)
decorate(inject(SessionService), AppService, i++)
decorate(inject(LogoutService), AppService, i++)
decorate(inject(DeviceTokenService), AppService, i++)
decorate(inject(PushService), AppService, i++)
decorate(inject(CurrentUserPollService), AppService, i++)
decorate(inject(UnreadCountsState), AppService, i++)
decorate(inject(Analytics), AppService, i++)
decorate(inject(ThemeState), AppService, i++)
decorate(inject(AuthBroadcastService), AppService, i++)
decorate(inject(IdleTimerService), AppService, i++)

export { AppService }
